/**
 * KpiSection component - Performance KPI configuration tab
 *
 * Two modes:
 * - Auto: System selects KPI based on campaign objective (no config needed)
 * - Manual: User picks primary KPI, optional secondary KPIs, optional benchmark
 *
 * KPI options grouped by category (Delivery, Traffic, Engagement, Video, Conversion, Derived).
 */

import {
  Activity,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { KPI_CATEGORIES, KPI_OPTIONS } from '@/constants/kpi-options';
import type { KpiConfig, KpiOption } from '@/types/template';

// ============================================================================
// TYPES
// ============================================================================

export interface KpiSectionProps {
  kpiConfig: KpiConfig | null;
  onChange: (config: KpiConfig) => void;
  customKpiOptions?: KpiOption[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Mirrors backend META_GOAL_KPI_MAP (backend/src/layer/shared/constants/kpi_goal_mappings.py).
// Phase 999.1: must stay in sync with backend — do not add/remove without backend change.
const AUTO_DETECT_MAPPINGS: { goal: string; kpi: string; description: string }[] = [
  { goal: 'ThruPlay', kpi: 'ThruPlay Rate', description: 'ThruPlays / impressions' },
  { goal: 'Conversions', kpi: 'CPA', description: 'Cost per acquisition' },
  { goal: 'Offsite Conversions', kpi: 'CPA', description: 'Cost per acquisition' },
  { goal: 'Link Clicks', kpi: 'CTR', description: 'Click-through rate' },
  { goal: 'Reach', kpi: 'CPM', description: 'Cost per 1,000 impressions' },
  { goal: 'Impressions', kpi: 'CPM', description: 'Cost per 1,000 impressions' },
  { goal: 'Lead Generation', kpi: 'CPA', description: 'Cost per lead' },
  { goal: 'Value', kpi: 'ROAS', description: 'Return on ad spend' },
  { goal: 'App Installs', kpi: 'CPA', description: 'Cost per install' },
  { goal: 'Landing Page Views', kpi: 'Cost per LPV', description: 'Cost per landing page view' },
];

// ============================================================================
// HELPERS
// ============================================================================

function groupKpisByCategory(kpiOptions: KpiOption[]): Partial<Record<string, KpiOption[]>> {
  const grouped: Partial<Record<string, KpiOption[]>> = {};
  for (const kpi of kpiOptions) {
    if (!grouped[kpi.category]) {
      grouped[kpi.category] = [];
    }
    grouped[kpi.category]!.push(kpi);
  }
  return grouped;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function KpiSection({ kpiConfig, onChange, customKpiOptions = [] }: KpiSectionProps) {
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const allKpiOptions = useMemo(
    () => [...KPI_OPTIONS, ...customKpiOptions],
    [customKpiOptions],
  );

  const allKpiById = useMemo(
    () => Object.fromEntries(allKpiOptions.map((kpi) => [kpi.id, kpi])),
    [allKpiOptions],
  );

  const grouped = useMemo(() => groupKpisByCategory(allKpiOptions), [allKpiOptions]);

  const primaryKpi = kpiConfig?.primaryKpiId
    ? allKpiById[kpiConfig.primaryKpiId] ?? null
    : null;

  const secondaryKpis = useMemo(
    () =>
      (kpiConfig?.secondaryKpiIds ?? [])
        .map((id) => allKpiById[id])
        .filter((k): k is KpiOption => k !== undefined),
    [allKpiById, kpiConfig?.secondaryKpiIds],
  );

  // --- Handlers ---

  function withCustomKpiVersions(config: KpiConfig): KpiConfig {
    const versions = kpiConfig?.customKpiVersions ?? {};
    const selectedCustomKpiIds = new Set(
      [config.primaryKpiId, ...config.secondaryKpiIds].filter(
        (id): id is string => typeof id === 'string' && id.startsWith('ckpi_')
      )
    );
    const customKpiVersions = Object.fromEntries(
      Object.entries(versions).filter(([id]) => selectedCustomKpiIds.has(id))
    );

    return Object.keys(customKpiVersions).length > 0
      ? { ...config, customKpiVersions }
      : config;
  }

  function toggleCategory(category: string) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }

  function handleModeSwitch(mode: 'auto' | 'manual') {
    if (mode === 'auto') {
      onChange(withCustomKpiVersions({
        mode: 'auto',
        primaryKpiId: null,
        secondaryKpiIds: [],
        benchmark: null,
      }));
    } else {
      onChange(withCustomKpiVersions({
        mode: 'manual',
        primaryKpiId: kpiConfig?.primaryKpiId ?? null,
        secondaryKpiIds: kpiConfig?.secondaryKpiIds ?? [],
        benchmark: kpiConfig?.benchmark ?? null,
      }));
    }
  }

  function handlePrimarySelect(kpiId: string) {
    onChange(withCustomKpiVersions({
      mode: 'manual',
      primaryKpiId: kpiId,
      secondaryKpiIds: (kpiConfig?.secondaryKpiIds ?? []).filter((id) => id !== kpiId),
      benchmark: null,
    }));
  }

  function handleSecondaryToggle(kpiId: string) {
    const current = kpiConfig?.secondaryKpiIds ?? [];
    let updated: string[];
    if (current.includes(kpiId)) {
      updated = current.filter((id) => id !== kpiId);
    } else if (current.length >= 2) {
      // Max 2 secondary KPIs — replace the last one
      updated = [current[0]!, kpiId];
    } else {
      updated = [...current, kpiId];
    }
    onChange(withCustomKpiVersions({
      mode: 'manual',
      primaryKpiId: kpiConfig?.primaryKpiId ?? null,
      secondaryKpiIds: updated,
      benchmark: kpiConfig?.benchmark ?? null,
    }));
  }

  function handleBenchmarkChange(value: string) {
    const parsed = parseFloat(value);
    onChange({
      ...(kpiConfig ?? { mode: 'manual', primaryKpiId: null, secondaryKpiIds: [] }),
      benchmark: isNaN(parsed) ? null : parsed,
    });
  }

  // --- Null config means KPIs not applicable (pre-flight mode) ---
  if (kpiConfig === null) {
    return (
      <div className="bg-warning/10 border border-warning/20 rounded-xl p-4 flex items-start gap-3">
        <Info size={16} className="text-warning flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-primary">Not applicable</p>
          <p className="text-sm text-secondary mt-1">
            This template evaluates creative quality only (pre-flight mode).
            Performance KPIs require a different measurement philosophy.
          </p>
        </div>
      </div>
    );
  }

  const currentMode = kpiConfig.mode;

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div>
        <label className="mb-2 block text-sm font-medium text-secondary">
          KPI Selection Mode
        </label>
        <div className="flex rounded-lg overflow-hidden border border-border-subtle w-fit">
          <button
            type="button"
            onClick={() => handleModeSwitch('auto')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              currentMode === 'auto'
                ? 'bg-brand text-white'
                : 'bg-surface text-secondary border-r border-border-subtle hover:bg-surface-subtle'
            }`}
          >
            Auto-detect
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch('manual')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              currentMode === 'manual'
                ? 'bg-brand text-white'
                : 'bg-surface text-secondary hover:bg-surface-subtle'
            }`}
          >
            Manual
          </button>
        </div>
      </div>

      {currentMode === 'auto' ? (
        /* Auto-detect content */
        <div className="space-y-3">
          <div className="bg-brand/10 border border-brand/20 rounded-xl p-4 flex items-start gap-3">
            <Activity size={16} className="text-brand flex-shrink-0 mt-0.5" />
            <p className="text-sm text-secondary">
              AdVerdict selects the most relevant KPI based on each ad set&apos;s optimization goal in Meta.
              No configuration needed.
            </p>
          </div>

          <div className="bg-surface rounded-xl border border-border-subtle overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-xs uppercase text-muted font-semibold text-left px-4 py-3">
                    Optimization Goal
                  </th>
                  <th className="text-xs uppercase text-muted font-semibold text-left px-4 py-3">
                    Primary KPI
                  </th>
                  <th className="text-xs uppercase text-muted font-semibold text-left px-4 py-3">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {AUTO_DETECT_MAPPINGS.map((row, i) => (
                  <tr
                    key={i}
                    className={`border-b border-border-subtle last:border-b-0 ${
                      i % 2 === 0 ? 'bg-surface' : 'bg-surface-subtle/40'
                    }`}
                  >
                    <td className="px-4 py-2.5 text-sm text-primary">{row.goal}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-brand font-semibold">{row.kpi}</span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-secondary">{row.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Manual mode content */
        <div className="space-y-4">
          {/* Column header labels */}
          <div className="flex items-center gap-3 px-1">
            <div className="flex-1" />
            <div className="flex gap-6 text-xs text-muted font-medium pr-1">
              <span>Main</span>
              <span className="w-[52px] text-center">Secondary</span>
            </div>
          </div>

          {/* KPI groups */}
          <div className="space-y-2">
            {KPI_CATEGORIES.filter((cat) => {
              const kpis = grouped[cat.key];
              return kpis && kpis.length > 0;
            }).map((cat) => {
              const kpis = grouped[cat.key]!;
              const isCollapsed = collapsedCategories.has(cat.key);

              return (
                <div key={cat.key} className="bg-surface rounded-xl border border-border-subtle overflow-hidden">
                  {/* Category header */}
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat.key)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-surface-subtle/50 transition-colors"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                      {cat.name}
                    </span>
                    {isCollapsed ? (
                      <ChevronDown size={14} className="text-muted" />
                    ) : (
                      <ChevronUp size={14} className="text-muted" />
                    )}
                  </button>

                  {/* KPI rows */}
                  {!isCollapsed && (
                    <div className="border-t border-border-subtle">
                      {kpis.map((kpi) => {
                        const isPrimary = kpiConfig.primaryKpiId === kpi.id;
                        const isSecondary = kpiConfig.secondaryKpiIds.includes(kpi.id);
                        const isDisabledSecondary = isPrimary;

                        return (
                          <div
                            key={kpi.id}
                            className={`flex items-center gap-3 py-2 px-4 hover:bg-surface-subtle/50 border-b border-border-subtle last:border-b-0 ${
                              isPrimary ? 'bg-brand/5' : ''
                            }`}
                          >
                            {/* KPI info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-primary">
                                  {kpi.shortLabel}
                                </span>
                                <span className="text-xs text-muted bg-surface-subtle px-1.5 py-0.5 rounded-md border border-border-subtle">
                                  {kpi.unit}
                                </span>
                                {!kpi.higherIsBetter && (
                                  <span className="text-xs text-muted italic">lower is better</span>
                                )}
                              </div>
                              <p className="text-xs text-muted mt-0.5 truncate">{kpi.description}</p>
                            </div>

                            {/* Controls: Primary radio + Secondary checkbox */}
                            <div className="flex items-center gap-6 flex-shrink-0">
                              <input
                                type="radio"
                                name="primaryKpi"
                                checked={isPrimary}
                                onChange={() => handlePrimarySelect(kpi.id)}
                                className="w-4 h-4 accent-brand cursor-pointer"
                                aria-label={`${kpi.shortLabel} primary KPI`}
                              />
                              <input
                                type="checkbox"
                                checked={isSecondary}
                                disabled={isDisabledSecondary}
                                onChange={() => handleSecondaryToggle(kpi.id)}
                                className="w-4 h-4 accent-brand cursor-pointer disabled:opacity-30"
                                aria-label={`${kpi.shortLabel} secondary KPI`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Benchmark input */}
          {primaryKpi && (
            <div className="bg-surface rounded-xl border border-border-subtle p-4 space-y-1.5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-primary">Benchmark (optional)</p>
                  <p className="text-xs text-muted mt-0.5">
                    Set a target value for {primaryKpi.label}. This is optional — the percentile
                    engine works without it.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <input
                    type="number"
                    value={kpiConfig.benchmark ?? ''}
                    onChange={(e) => handleBenchmarkChange(e.target.value)}
                    placeholder="--"
                    className="border border-border-subtle rounded-lg px-3 py-2 text-sm bg-surface w-32 text-primary focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                  />
                  <span className="text-sm text-secondary font-medium">{primaryKpi.unit}</span>
                </div>
              </div>
            </div>
          )}

          {/* Summary bar */}
          {(primaryKpi || secondaryKpis.length > 0) && (
            <div className="bg-surface-subtle rounded-lg p-3 text-sm text-secondary">
              <span className="font-medium text-primary">Summary: </span>
              {primaryKpi && (
                <span>
                  Main:{' '}
                  <span className="text-brand font-medium">{primaryKpi.shortLabel}</span>
                </span>
              )}
              {secondaryKpis.length > 0 && (
                <span>
                  {primaryKpi ? ' | ' : ''}Secondary:{' '}
                  <span className="text-primary font-medium">
                    {secondaryKpis.map((k) => k.shortLabel).join(', ')}
                  </span>
                </span>
              )}
              {kpiConfig.benchmark !== null && primaryKpi && (
                <span>
                  {' '}
                  | Benchmark:{' '}
                  <span className="text-primary font-medium">
                    {kpiConfig.benchmark}
                    {primaryKpi.unit}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
